<template>
  <DialogHeader>
    Add copyright owner
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSave"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
  </DialogHeader>
  <DialogContent>
    <CopyrightOwnerTypeSelection v-model="type" class="mb-[14px]" />

    <UiInput
      v-if="isPersonal"
      v-model="name"
      :label="label"
      :disabled="loading"
    />

    <SearchOrganizations
      v-else
      v-model="selectedOrg"
      :label="label"
      :disabled="loading"
      @update:query="handleNameUpdate"
    />
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      primary
      :disabled="loading"
      :loading="loading"
      @click="handleSave"
    >
      Save
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, UiButton, UiInput } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { computed, ref } from 'vue'
import {
  CopyrightOwnerType,
  type Organization,
  useAddCopyrightOwnerMutation,
  useAddCopyrightOwnerEntityMutation,
  EntityType,
  type User,
  type CopyrightOwnersNewQuery,
  CopyrightOwnersNewDocument,
} from '@/shared/graphql/legacy/generated'
import CopyrightOwnerTypeSelection from '@/features/copyright/components/CopyrightOwnerTypeSelection.vue'
import SearchOrganizations from '@/features/search/components/SearchOrganizations.vue'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import gql from 'graphql-tag'

const { close } = useDialog()

const { loading: addCopyrightOwnerLoading, mutate: addCopyrightOwner } =
  useAddCopyrightOwnerMutation()

const {
  loading: addCopyrightOwnerEntityLoading,
  mutate: addCopyrightOwnerEntity,
} = useAddCopyrightOwnerEntityMutation()

const toastStore = useToastStore()
const userStore = useUserStore()

const name = ref('')
const selectedOrg = ref<Organization>()
const type = ref<CopyrightOwnerType>(CopyrightOwnerType.Personal)

const isPersonal = computed(() => type.value === CopyrightOwnerType.Personal)

const label = computed(() =>
  type.value === CopyrightOwnerType.Personal ? 'Name' : 'Orgnaization name'
)

const loading = computed(
  () => addCopyrightOwnerLoading.value || addCopyrightOwnerEntityLoading.value
)

const handleNameUpdate = (value: string) => {
  name.value = value
}

const handleSave = async () => {
  try {
    if (name.value) {
      await addCopyrightOwner(
        {
          name: name.value,
          type: type.value,
        },
        {
          update(cache, { data }) {
            cache.updateQuery<CopyrightOwnersNewQuery>(
              { query: CopyrightOwnersNewDocument },
              (response) => {
                if (!response || !data) return

                return {
                  ...response,
                  copyrightOwnersNew: {
                    ...response.copyrightOwnersNew,
                    items: response.copyrightOwnersNew.items.concat({
                      __typename: 'CopyrightOwnerNew',
                      id: data.addCopyrightOwnerNew.id,
                      name: data.addCopyrightOwnerNew.name,
                      avatarUrl: data.addCopyrightOwnerNew.avatarUrl,
                      isSelected: data.addCopyrightOwnerNew.isSelected,
                      type: data.addCopyrightOwnerNew.type,
                      owner: data.addCopyrightOwnerNew.owner,
                    }),
                    total: response.copyrightOwnersNew.total + 1,
                  },
                }
              }
            )

            cache.updateFragment<User>(
              {
                id: cache.identify({
                  __typename: 'User',
                  id: userStore.user?.id,
                }),
                fragment: gql`
                  fragment UpdateCopyrightOwnersTotal on User {
                    totalCopyrightOwners
                  }
                `,
              },
              (response) => {
                if (!response) return

                return {
                  ...response,
                  totalCopyrightOwners:
                    (response.totalCopyrightOwners || 0) + 1,
                }
              }
            )
          },
        }
      )
    }

    if (selectedOrg.value) {
      await addCopyrightOwnerEntity(
        {
          input: {
            id: selectedOrg.value.id,
            type: EntityType.Organization,
          },
        },
        {
          update(cache, { data }) {
            cache.updateQuery<CopyrightOwnersNewQuery>(
              { query: CopyrightOwnersNewDocument },
              (response) => {
                if (!response || !data) return

                return {
                  ...response,
                  copyrightOwnersNew: {
                    ...response.copyrightOwnersNew,
                    items: response.copyrightOwnersNew.items.concat({
                      __typename: 'CopyrightOwnerNew',
                      id: data.addCopyrightOwnerEntityNew.id,
                      name: data.addCopyrightOwnerEntityNew.name,
                      avatarUrl: data.addCopyrightOwnerEntityNew.avatarUrl,
                      isSelected: data.addCopyrightOwnerEntityNew.isSelected,
                      type: data.addCopyrightOwnerEntityNew.type,
                      owner: data.addCopyrightOwnerEntityNew.owner,
                    }),
                    total: response.copyrightOwnersNew.total + 1,
                  },
                }
              }
            )

            cache.updateFragment<User>(
              {
                id: cache.identify({
                  __typename: 'User',
                  id: userStore.user?.id,
                }),
                fragment: gql`
                  fragment UpdateCopyrightOwnersTotal on User {
                    totalCopyrightOwners
                  }
                `,
              },
              (response) => {
                if (!response) return

                return {
                  ...response,
                  totalCopyrightOwners:
                    (response.totalCopyrightOwners || 0) + 1,
                }
              }
            )
          },
        }
      )
    }
  } catch (error) {
    console.error(error)

    let message = 'Something went wrong - please try again later.'

    if ((error as Error).message.toLowerCase().includes('max')) {
      message =
        'You cannot add more than 2 copyright owners. Please ugprade to Pro to add more.'
    }

    toastStore.showToast({
      type: ToastType.Error,
      content: message,
      error: error as Error,
    })
  }

  close()
}
</script>
